import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import SectionHeading from '../../components/common/SectionHeading';
import useApi from '../../hooks/useApi';

const PORTAL_USER_KEY = 'portal_user';
const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5003';

const Wrapper = styled.section`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 1.5rem;
  padding: 2.5rem;
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

const SectionBlock = styled.section`
  margin-top: 1.5rem;
`;

const SubTitle = styled.h3`
  margin: 0 0 0.8rem;
  font-size: 1.05rem;
`;

const Tabs = styled.div`
  display: inline-flex;
  gap: 0.5rem;
  margin-bottom: 0.9rem;
`;

const TabButton = styled.button`
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 999px;
  padding: 0.45rem 0.85rem;
  background: ${({ $active }) => ($active ? 'rgba(37, 99, 235, 0.18)' : 'transparent')};
  color: ${({ $active }) => ($active ? '#1d4ed8' : '#64748b')};
  font-weight: 700;
  cursor: pointer;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid ${({ theme }) => theme.colors.surfaceMuted};
  border-radius: 0.8rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 760px;
`;

const Th = styled.th`
  text-align: left;
  padding: 0.7rem;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.textMuted};
  border-bottom: 1px solid ${({ theme }) => theme.colors.surfaceMuted};
  background: rgba(15, 23, 42, 0.03);
`;

const Td = styled.td`
  padding: 0.7rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.surfaceMuted};
  vertical-align: top;
  font-size: 0.93rem;
`;

const InlineEditor = styled.textarea`
  width: 100%;
  min-height: 76px;
  resize: vertical;
  border-radius: 0.6rem;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(15, 23, 42, 0.04);
  color: ${({ theme }) => theme.colors.text};
  padding: 0.55rem 0.65rem;
  line-height: 1.4;
`;

const InlineActions = styled.div`
  margin-top: 0.45rem;
  display: flex;
  justify-content: flex-end;
`;

const EmptyRow = styled.p`
  margin: 0.9rem 0 0;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const SaveButton = styled.button`
  border: 0;
  border-radius: 0.65rem;
  padding: 0.42rem 0.72rem;
  background: #2563eb;
  color: #fff;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.75 : 1)};
`;

const Message = styled.p`
  margin: 0.9rem 0 0;
  min-height: 1.2rem;
  color: ${({ error }) => (error ? '#fca5a5' : '#93c5fd')};
`;

const readPortalUser = () => {
  try {
    const raw = sessionStorage.getItem(PORTAL_USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && Array.isArray(parsed.roles) ? parsed : null;
  } catch {
    return null;
  }
};

const PortalStaffPage = () => {
  const { request, loading } = useApi();
  const portalUser = useMemo(() => readPortalUser(), []);
  const [activeRole, setActiveRole] = useState('student');
  const [users, setUsers] = useState([]);
  const [draftMessages, setDraftMessages] = useState({});
  const [savingUserId, setSavingUserId] = useState(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchUsers = async () => {
      if (!portalUser?.login_id) return;
      try {
        const data = await request({
          endpoint: `${BACKEND_BASE_URL}/api/admin/daily-message-users?login_id=${encodeURIComponent(portalUser.login_id)}&role=${activeRole}`,
        });
        const nextUsers = Array.isArray(data?.users) ? data.users : [];
        setUsers(nextUsers);
        setDraftMessages(
          nextUsers.reduce((acc, user) => {
            acc[user.id] = user.daily_message || '';
            return acc;
          }, {})
        );
      } catch (err) {
        setUsers([]);
        setMessage(err?.responseData?.message || '사용자 목록을 불러오지 못했습니다.');
      }
    };
    fetchUsers();
  }, [portalUser?.login_id, activeRole, request]);

  const handleSaveUserMessage = async (user) => {
    if (!portalUser?.login_id || !user?.id) return;
    const nextMessage = String(draftMessages[user.id] || '').trim();
    if (!nextMessage) {
      setMessage('메시지를 입력해 주세요.');
      return;
    }
    try {
      setSavingUserId(user.id);
      await request({
        endpoint: `${BACKEND_BASE_URL}/api/admin/daily-message-users/${user.id}`,
        method: 'POST',
        body: {
          login_id: portalUser.login_id,
          daily_message: nextMessage,
        },
      });
      setUsers((prev) =>
        prev.map((item) =>
          item.id === user.id
            ? { ...item, daily_message: nextMessage, daily_message_date: new Date().toISOString().slice(0, 10) }
            : item
        )
      );
      setMessage('개별 오늘의 메시지가 저장되었습니다.');
    } catch (err) {
      setMessage(err?.responseData?.message || '저장에 실패했습니다.');
    } finally {
      setSavingUserId(null);
    }
  };

  return (
    <Wrapper>
      <SectionHeading
        eyebrow="ADMIN"
        title="관리자 포털"
        description="포털 홈에 보이는 오늘의 인사 문구를 역할별로 지정할 수 있어요."
      />
      <SectionBlock>
        <SubTitle>오늘의 메세지 관리</SubTitle>
        <Tabs>
          <TabButton type="button" $active={activeRole === 'student'} onClick={() => setActiveRole('student')}>
            학생
          </TabButton>
          <TabButton type="button" $active={activeRole === 'parent'} onClick={() => setActiveRole('parent')}>
            학부모
          </TabButton>
          <TabButton type="button" $active={activeRole === 'teacher'} onClick={() => setActiveRole('teacher')}>
            강사
          </TabButton>
        </Tabs>
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <Th>이름</Th>
                <Th>아이디</Th>
                <Th>연락처</Th>
                {activeRole === 'parent' && <Th>학생</Th>}
                <Th>오늘의 메세지</Th>
                <Th>적용일</Th>
                <Th>저장</Th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <Td>{user.name || '-'}</Td>
                  <Td>{user.login_id || '-'}</Td>
                  <Td>{user.phone || '-'}</Td>
                  {activeRole === 'parent' && <Td>{user.child_students || '-'}</Td>}
                  <Td>
                    <InlineEditor
                      value={draftMessages[user.id] || ''}
                      onChange={(e) =>
                        setDraftMessages((prev) => ({
                          ...prev,
                          [user.id]: e.target.value,
                        }))
                      }
                    />
                  </Td>
                  <Td>{user.daily_message_date || '-'}</Td>
                  <Td>
                    <InlineActions>
                      <SaveButton
                        type="button"
                        onClick={() => handleSaveUserMessage(user)}
                        disabled={loading || savingUserId === user.id}
                      >
                        {savingUserId === user.id ? '저장 중...' : '저장'}
                      </SaveButton>
                    </InlineActions>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
        {!loading && users.length === 0 && <EmptyRow>조회된 사용자가 없습니다.</EmptyRow>}
      </SectionBlock>
      <Message error={/실패|오류|못|권한/.test(message)}>{message}</Message>
    </Wrapper>
  );
};

export default PortalStaffPage;
